"use client";

import { useMemo, useState } from "react";
import useSWR from "swr";
import { ChevronRight, Loader2, RotateCcw, Save } from "lucide-react";
import { useCollaboration } from "@workspace/collaboration/provider";
import { createVersionsClient, type VersionMeta } from "@workspace/collaboration/snapshot";
import { UserAvatar } from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import { Input } from "@workspace/ui/components/input";
import { cn } from "@workspace/ui/lib/utils";
import { apiClient } from "@/lib/api-client";
import type { EditHunk } from "@/lib/edits/diff";
import {
  rollUpHistory,
  type EditAuthor,
  type EditRecord,
  type HistoryGroup,
} from "@/lib/edits/rollup";

interface HistoryProps {
  documentId: string;
}

/**
 * Version history as a history of edits.
 *
 * Each version snapshot is a collapsible group of the attributed edits that
 * led to it (see `lib/edits/rollup.ts`). Edits made since the latest version sit
 * in an unsaved group at the top. Save and restore still go through the
 * snapshot endpoints. Edits are for reading, versions are for restoring.
 */
export function History({ documentId }: HistoryProps) {
  const { ydoc } = useCollaboration();
  const client = useMemo(
    () => createVersionsClient(`/api/versions/${documentId}`),
    [documentId],
  );

  // Edits land ~30s after someone stops typing, so a slow poll keeps the panel
  // honest without a push channel.
  const versions = useSWR<VersionMeta[]>(["versions", documentId], () => client.list(), {
    refreshInterval: 60_000,
  });
  const edits = useSWR<EditRecord[]>(
    `/api/documents/${documentId}/edits`,
    (url: string) => apiClient.get<EditRecord[]>(url).then((r) => r.data),
    { refreshInterval: 30_000 },
  );

  const groups = useMemo(
    () =>
      rollUpHistory(
        (versions.data ?? []).map((v) => ({ ...v, authorId: v.authorId ?? "system" })),
        edits.data ?? [],
      ),
    [versions.data, edits.data],
  );

  const [restoring, setRestoring] = useState<string | null>(null);
  const restore = async (id: string) => {
    if (!ydoc) return;
    if (!confirm("Restore this version? Its contents will be merged into the current document.")) {
      return;
    }
    setRestoring(id);
    try {
      await client.restore(ydoc, id);
    } finally {
      setRestoring(null);
    }
  };

  const loading = !versions.data && !edits.data && (versions.isLoading || edits.isLoading);

  return (
    <div className="flex flex-col min-h-0 h-full">
      {ydoc && (
        <SaveVersion
          onSave={async (label) => {
            await client.save(ydoc, { label });
            await versions.mutate();
          }}
        />
      )}

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {loading ? (
          <div className="flex justify-center p-6">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : groups.length === 0 ? (
          <p className="p-4 text-xs text-muted-foreground text-center">
            No edits or versions yet.
          </p>
        ) : (
          <ol className="divide-y">
            {groups.map((group, i) => (
              <GroupRow
                key={group.version?.id ?? "current"}
                group={group}
                defaultOpen={i === 0}
                restoring={restoring === group.version?.id}
                onRestore={ydoc && group.version ? () => restore(group.version!.id) : undefined}
              />
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function SaveVersion({ onSave }: { onSave: (label?: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(label.trim() || undefined);
      setOpen(false);
      setLabel("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 border-b shrink-0">
      {open ? (
        <div className="flex gap-2">
          <Input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") setOpen(false);
            }}
            placeholder="Version label (optional)"
            className="h-8 text-xs"
          />
          <Button size="sm" className="h-8" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" className="w-full h-8" onClick={() => setOpen(true)}>
          <Save className="h-3.5 w-3.5 mr-1.5" />
          Save version
        </Button>
      )}
    </div>
  );
}

const TRIGGER_LABEL: Record<string, string> = {
  manual: "Saved version",
  "auto-idle": "Auto-saved",
  "auto-force": "Auto-saved",
  "auto-disconnect": "Auto-saved",
};

function GroupRow({
  group,
  defaultOpen,
  restoring,
  onRestore,
}: {
  group: HistoryGroup;
  defaultOpen: boolean;
  restoring: boolean;
  onRestore?: () => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const { version, edits, authors, totals } = group;
  const title = version
    ? version.label || TRIGGER_LABEL[version.trigger] || "Version"
    : "Unsaved changes";
  const expandable = edits.length > 0;

  return (
    <li>
      <div className="flex items-start gap-2 p-3">
        <button
          type="button"
          onClick={() => expandable && setOpen(!open)}
          aria-expanded={expandable ? open : undefined}
          disabled={!expandable}
          className="flex flex-1 min-w-0 items-start gap-1.5 text-left disabled:cursor-default"
        >
          <ChevronRight
            className={cn(
              "h-4 w-4 mt-0.5 shrink-0 text-muted-foreground transition-transform",
              open && expandable && "rotate-90",
              !expandable && "invisible",
            )}
          />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">{title}</span>
              {!version && (
                <span className="size-1.5 rounded-full bg-orange-500 shrink-0" aria-hidden />
              )}
            </span>
            <span className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
              <time dateTime={new Date(group.at).toISOString()} title={new Date(group.at).toLocaleString()}>
                {relativeTime(group.at)}
              </time>
              {expandable && (
                <>
                  <span aria-hidden>·</span>
                  <span>
                    {edits.length} edit{edits.length === 1 ? "" : "s"}
                  </span>
                  <Totals totals={totals} />
                </>
              )}
            </span>
          </span>
        </button>

        <div className="flex items-center gap-1.5 shrink-0">
          <AvatarStack authors={authors} />
          {onRestore && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onRestore}
              disabled={restoring}
              aria-label={`Restore ${title}`}
              title="Restore this version"
            >
              {restoring ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
        </div>
      </div>

      {open && expandable && (
        <ol className="pb-3 pl-6 pr-3 space-y-3">
          {edits.map((edit) => (
            <EditRow key={edit.id} edit={edit} />
          ))}
        </ol>
      )}
    </li>
  );
}

function EditRow({ edit }: { edit: EditRecord }) {
  // Every total has exactly one hunk; the server keeps only the first few.
  const total = edit.totals.added + edit.totals.removed + edit.totals.changed;
  const hidden = Math.max(0, total - edit.hunks.length);

  return (
    <li className="relative border-l pl-3">
      <div className="flex items-center gap-2 mb-1.5">
        <AvatarStack authors={edit.authors} size="xs" />
        <span className="text-xs font-medium truncate">
          {edit.authors.map((a) => a.name).join(", ") || "Unknown"}
        </span>
        <time
          className="ml-auto text-[10px] text-muted-foreground shrink-0"
          dateTime={new Date(edit.endedAt).toISOString()}
          title={new Date(edit.endedAt).toLocaleString()}
        >
          {clockTime(edit.endedAt)}
        </time>
      </div>
      <ul className="space-y-1">
        {edit.hunks.map((hunk, i) => (
          <HunkRow key={i} hunk={hunk} />
        ))}
      </ul>
      {hidden > 0 && (
        <p className="mt-1 text-[10px] text-muted-foreground">
          +{hidden} more change{hidden === 1 ? "" : "s"}
        </p>
      )}
    </li>
  );
}

const HUNK_STYLE: Record<EditHunk["kind"], string> = {
  added: "border-green-500 bg-green-500/10",
  removed: "border-red-500 bg-red-500/10",
  changed: "border-orange-500 bg-orange-500/10",
};

function HunkRow({ hunk }: { hunk: EditHunk }) {
  return (
    <li className={cn("rounded-r border-l-2 px-2 py-1 text-xs leading-relaxed", HUNK_STYLE[hunk.kind])}>
      {hunk.section && (
        <span className="block text-[10px] text-muted-foreground truncate">§ {hunk.section}</span>
      )}
      <span className="sr-only">{hunk.kind}: </span>
      {hunk.kind === "changed" ? (
        <span className="break-words">
          {hunk.segments.map((seg, i) =>
            seg.op === "eq" ? (
              <span key={i} className="text-muted-foreground">
                {seg.text}
              </span>
            ) : seg.op === "del" ? (
              <del key={i} className="text-red-700 dark:text-red-400 bg-red-500/15 rounded-sm">
                {seg.text}
              </del>
            ) : (
              <ins key={i} className="no-underline text-green-700 dark:text-green-400 bg-green-500/15 rounded-sm">
                {seg.text}
              </ins>
            ),
          )}
        </span>
      ) : (
        <span
          className={cn(
            "break-words",
            hunk.kind === "removed" && "line-through text-red-700 dark:text-red-400",
            hunk.kind === "added" && "text-green-700 dark:text-green-400",
            /^h[1-6]$/.test(hunk.block) && "font-semibold",
          )}
        >
          {hunk.text}
        </span>
      )}
    </li>
  );
}

function Totals({ totals }: { totals: HistoryGroup["totals"] }) {
  return (
    <span className="flex items-center gap-1.5 font-medium tabular-nums">
      {totals.added > 0 && <span className="text-green-600 dark:text-green-400">+{totals.added}</span>}
      {totals.removed > 0 && <span className="text-red-600 dark:text-red-400">−{totals.removed}</span>}
      {totals.changed > 0 && <span className="text-orange-600 dark:text-orange-400">~{totals.changed}</span>}
    </span>
  );
}

function AvatarStack({ authors, size = "sm" }: { authors: EditAuthor[]; size?: "xs" | "sm" }) {
  if (authors.length === 0) return null;
  const shown = authors.slice(0, 3);
  const dim = size === "xs" ? "h-5 w-5 text-[9px]" : "h-6 w-6 text-[10px]";
  return (
    <span className="flex -space-x-1.5" title={authors.map((a) => a.name).join(", ")}>
      {shown.map((a) => (
        <UserAvatar
          key={a.id}
          username={a.name}
          email={a.email ?? ""}
          size="sm"
          className={cn(dim, "ring-2 ring-background")}
        />
      ))}
      {authors.length > shown.length && (
        <span
          className={cn(
            dim,
            "flex items-center justify-center rounded-full bg-muted ring-2 ring-background font-medium",
          )}
        >
          +{authors.length - shown.length}
        </span>
      )}
    </span>
  );
}

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

function relativeTime(ms: number): string {
  const diff = (ms - Date.now()) / 1000;
  const abs = Math.abs(diff);
  if (abs < 60) return "just now";
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86_400) return rtf.format(Math.round(diff / 3600), "hour");
  if (abs < 7 * 86_400) return rtf.format(Math.round(diff / 86_400), "day");
  return new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function clockTime(ms: number): string {
  const d = new Date(ms);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}
